import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import controlIds from '../constants/control-ids';
import computeCornersFallback from '../helpers/compute-corners-fallback';

const {
  Context: ElementSettingsContext,
  components: { ToggleableSection },
  helpers: { getBatchValues },
  verifiers: { hasWiderBreakpointValue },
} = elementSettings;

// We need two separate values to correctly display and distinguish between the values set by the toggle and the slider, because a zero from either one looks the same.
//
// The slider writes `0px`. Turning Corners off writes a unitless `0`, or removes the value when there is nothing to override.
//
// This distinction can be removed once element settings use accordions instead of toggles for option groups.
const OFF_VALUE = '0';

function CornersSectionControl({ elementIds, targets, onChange, children }) {
  const { state, resolvedState, deviceMode } = useContext(ElementSettingsContext);

  const isToggleOn = useMemo(() => {
    const values = getBatchValues(resolvedState, {
      elementIds,
      targets,
    }).flat();

    return values.some((value) => value?.endsWith('px'));
  }, [resolvedState, elementIds, targets]);

  const handleToggleChange = useCallback(
    (isActive) => {
      const breakpointKey = deviceMode?.breakpointKey;

      const computeValue = ({ elementId, target }) => {
        if (isActive) return computeCornersFallback({ elementId, target });

        return hasWiderBreakpointValue({ state, elementId, target, breakpointKey })
          ? OFF_VALUE
          : undefined;
      };

      const batch = Object.fromEntries(
        elementIds.map((elementId) => [
          elementId,
          Object.fromEntries(
            targets.map((target) => [target, computeValue({ elementId, target })])
          ),
        ])
      );

      onChange({ batch, controlId: controlIds.CORNERS_SECTION, value: isActive });
    },
    [deviceMode, elementIds, onChange, state, targets]
  );

  return (
    <ToggleableSection
      value={isToggleOn}
      title={i18next.t('Corners')}
      onChange={handleToggleChange}
    >
      {children}
    </ToggleableSection>
  );
}

CornersSectionControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default CornersSectionControl;
